//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import angular, {IScope} from 'angular';
import _ from 'underscore';

import template from './showroom-settings-filters-products.html';

class ShowroomSettingsFiltersProducts {
  public startingProducts;
  public onProductsChanged;

  public editingProduct: boolean;
  public productToEdit;
  public productEditingFeatures;

  public productList;
  public isShowroomSelfServeV2Enabled: boolean;
  public trackingCategory: string;
  public trackingAction: string;
  public trackingLabels;
  public PRODUCT_TYPES;

  constructor(
        private $scope: IScope,
        private AccountSettingsService,
        private ShowroomService,
        private TrackingService,
        private SessionVarsService,
    ) { }

  public $onInit() {
    this.isShowroomSelfServeV2Enabled = this.SessionVarsService.isFeatureEnabled('showroom_self_serve_v2');
    this.trackingCategory = this.ShowroomService.getTrackingCategory();
    this.trackingAction = this.ShowroomService.getTrackingAction();
    this.trackingLabels = {
      productAdded: 'CuratedProductAdded',
      dupeProductAdded: 'DuplicateCuratedProductAdded',
      productEdited: 'CuratedProductEdited',
      productRemoved: 'CuratedProductRemoved',
      productsCleared: 'CuratedProductsCleared',
      productReordered: 'CuratedProductsReordered',
      helpLinkClicked: (linkName) => `HelpLinkClicked=${linkName}`,
    };

    this.PRODUCT_TYPES = {
      CRAWL: 'Crawl',
      UPDATE_CRAWL: 'UpdateCrawl',
    };

    const copiedProductList = this.startingProducts ? angular.copy(this.startingProducts) : [];
    this.productList = _(copiedProductList).map((product: any, index) => {
      product.position = index;
      product.isCrawled = this.isProductCrawled(product);
      return product;
    });
    this.productEditingFeatures = { disableUrlEditing: true };

    this.editingProduct = false;
  }

  public onProductSelected(product) {
    const isDuplicate = !!_(this.productList).findWhere({ productStyleId: product.productStyleId });

    if (isDuplicate) {
      this.TrackingService.recordEvent(
                this.trackingCategory,
                this.trackingAction,
                this.trackingLabels.dupeProductAdded,
            );
    } else {
      this.TrackingService.recordEvent(
                this.trackingCategory,
                this.trackingAction,
                this.trackingLabels.productAdded,
            );
      product.position = this.productList.length;
      product.isCrawled = this.isProductCrawled(product);
      this.productList.push(product);
      this.updateProducts();
    }
  }

  public removeProduct(removeProduct) {
    this.TrackingService.recordEvent(
            this.trackingCategory,
            this.trackingAction,
            this.trackingLabels.productRemoved,
        );
    const listWithoutProduct = _.chain(this.productList).reject((product: any) => {
            return product.position === removeProduct.position;
        }).sortBy((product: any) => {
            return product.position;
        }).value();

    this.productList = _(listWithoutProduct).map((product: any, index) => {
      product.position = index;
      return product;
    });

    this.updateProducts();
  }

  public beginEditingProduct(product) {
    this.editingProduct = true;
    this.productToEdit = product;
  }

  public saveProductEdits(updatedProduct) {
    if (!angular.equals(updatedProduct, this.productToEdit)) {
      const updateIndex = _(this.productList).findIndex((product: any) => {
        return product.productStyleId === updatedProduct.productStyleId;
      });

      this.productList[updateIndex] = updatedProduct;

      this.TrackingService.recordEvent(
                this.trackingCategory,
                this.trackingAction,
                this.trackingLabels.productEdited,
            );

      this.updateProducts();
    }

    this.resetEditingState();
  }

  public cancelEditingCallback() {
    this.resetEditingState();
  }

  public clearAllProducts() {
    this.TrackingService.recordEvent(
            this.trackingCategory,
            this.trackingAction,
            this.trackingLabels.productsCleared,
        );
    this.productList = [];
    this.updateProducts();
  }

    //
    // Drag and drop functions
    //

  public swapProducts(dragProduct, swapProduct) {
    const dragPosition = dragProduct.position;
    const swapPosition = swapProduct.position;

        // Since the swapping here is being done by jQuery,
        // force an angular digest to update the UI in real time.
    this.$scope.$apply(() => {
      dragProduct.position = swapPosition;
      swapProduct.position = dragPosition;
    });
  }

  public dragEnded() {
    this.productList = _(this.productList).sortBy('position');
    this.TrackingService.recordEvent(
            this.trackingCategory,
            this.trackingAction,
            this.trackingLabels.productReordered,
        );
    this.updateProducts();
  }

    //
    // Private Helpers
    //

  private updateProducts() {
        // Clear out the list of registered items in the drag-sort directive
        // and update the product list, so all items are re-registered
    this.$scope.$broadcast('dragSort:itemsChanged', []);
    this.productList = angular.copy(this.productList);

    this.onProductsChanged(this.productList);
  }

  private isProductCrawled(product) {
    return product.productType &&
            (product.productType === this.PRODUCT_TYPES.CRAWL ||
             product.productType === this.PRODUCT_TYPES.UPDATE_CRAWL);
  }

  private resetEditingState() {
    this.editingProduct = false;
    delete this.productToEdit;
  }
}

angular.module('uia').component('showroomSettingsFiltersProducts', {
  bindings: {
    hasProductCatalog: '<',
    onProductsChanged: '<',
    startingProducts: '<',
  },
  template,
  controller: [
    '$scope',
    'AccountSettingsService',
    'ShowroomService',
    'TrackingService',
    'SessionVarsService',
    ShowroomSettingsFiltersProducts,
  ],
});
